import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from '../../config/axios';

interface Lead {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  source: string;
  crm_stages?: {
    id: string;
    name: string;
    crm_pipelines?: {
      id: string;
      name: string;
    };
  };
  created_at: string;
}

interface CampaignLead {
  id: string;
  campaign_id: string;
  contact_id: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'retry';
  attempts: number;
  sent_at?: string;
  delivered_at?: string;
  error_message?: string;
  created_at: string;
  contact: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    source: string;
    created_at: string;
  };
}

interface Campaign {
  id: string;
  name: string;
  description: string;
  message: string;
  channel: string;
  crm_stage_id?: string;
  scheduled_at?: string;
  settings: {
    enablePersonalization: boolean;
    enableTracking: boolean;
    enableRetry: boolean;
    maxRetries: number;
    delayBetweenMessages: number;
    sendInBatches: boolean;
    batchSize: number;
    batchDelay: number;
  };
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'failed';
  leads_count?: number;
}

interface CrmPipeline {
  id: string;
  name: string;
  stages: {
    id: string;
    name: string;
    color: string;
  }[];
}

const DisparosEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [campaignLeads, setCampaignLeads] = useState<CampaignLead[]>([]);
  const [crmPipelines, setCrmPipelines] = useState<CrmPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAllLeads, setShowAllLeads] = useState(false);

  // Carregar dados da campanha
  useEffect(() => {
    if (id) {
      fetchCampaign();
      fetchAvailableLeads();
      fetchCrmPipelines();
    }
  }, [id]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`/api/v1/disparos/${id}`);
      setCampaign(response.data.data);
      
      // Carregar leads da campanha
      const leadsResponse = await axios.get(`/api/v1/disparos/${id}/leads`);
      console.log('📊 Leads da campanha recebidos:', leadsResponse.data.data);
      setCampaignLeads(leadsResponse.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar campanha:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar campanha' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableLeads = async () => {
    try {
      console.log('🔍 Carregando leads disponíveis...');
      const response = await axios.get('/api/v1/contacts');
      
      // Adaptar para ambos os formatos (antigo e novo)
      let leads = [];
      if (response.data && Array.isArray(response.data)) {
        // Formato antigo: array direto
        leads = response.data;
      } else if (response.data && response.data.data) {
        // Formato novo: { success: true, data: [...] }
        leads = response.data.data;
      }
      
      console.log('📊 Leads carregados:', leads.length, leads);
      setAvailableLeads(leads);
    } catch (error) {
      console.error('❌ Erro ao carregar leads:', error);
    }
  };

  const fetchCrmPipelines = async () => {
    try {
      const response = await axios.get('/api/v1/crm/pipelines');
      setCrmPipelines(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar pipelines:', error);
    }
  };

  const handleLeadSelection = (leadId: string, selected: boolean) => {
    if (selected) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleAddLeads = async () => {
    if (selectedLeads.length === 0) {
      setMessage({ type: 'error', text: 'Selecione pelo menos um lead' });
      return;
    }

    // Validar se os IDs selecionados existem nos leads disponíveis
    const validLeadIds = availableLeads.map(lead => lead.id);
    const invalidIds = selectedLeads.filter(id => !validLeadIds.includes(id));
    
    if (invalidIds.length > 0) {
      console.error('IDs inválidos detectados:', invalidIds);
      setMessage({ type: 'error', text: 'Alguns leads selecionados são inválidos. Recarregue a página e tente novamente.' });
      return;
    }

    setSaving(true);
    try {
      console.log('Tentando adicionar leads:', {
        campaignId: id,
        contactIds: selectedLeads,
        url: `/api/v1/disparos/${id}/leads`,
        validLeads: selectedLeads.length,
        availableLeads: availableLeads.length
      });

      const response = await axios.post(`/api/v1/disparos/${id}/leads`, {
        contactIds: selectedLeads
      });

      console.log('Resposta do servidor:', response.data);
      
      // Recarregar leads da campanha
      const leadsResponse = await axios.get(`/api/v1/disparos/${id}/leads`);
      setCampaignLeads(leadsResponse.data.data || []);
      
      setSelectedLeads([]);
      setMessage({ type: 'success', text: 'Leads adicionados com sucesso!' });
    } catch (error: any) {
      console.error('Erro ao adicionar leads:', error);
      
      // Log detalhado do erro
      if (error.response) {
        console.error('Detalhes do erro:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        
        setMessage({ 
          type: 'error', 
          text: `Erro ${error.response.status}: ${error.response.data?.error || error.response.statusText}` 
        });
      } else if (error.request) {
        console.error('Erro de rede:', error.request);
        setMessage({ type: 'error', text: 'Erro de conexão com o servidor' });
      } else {
        console.error('Erro:', error.message);
        setMessage({ type: 'error', text: `Erro: ${error.message}` });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLead = async (leadId: string) => {
    try {
      await axios.delete(`/api/v1/disparos/${id}/leads/${leadId}`);
      setCampaignLeads(prev => prev.filter(lead => lead.id !== leadId));
      setMessage({ type: 'success', text: 'Lead removido com sucesso!' });
    } catch (error) {
      console.error('Erro ao remover lead:', error);
      setMessage({ type: 'error', text: 'Erro ao remover lead' });
    }
  };

  // Organizar leads por estágio do CRM
  const leadsByStage = availableLeads.reduce((acc, lead) => {
    const stageId = lead.crm_stages?.id || 'sem-estagio';
    const stageName = lead.crm_stages?.name || 'Sem Estágio';
    const pipelineName = lead.crm_stages?.crm_pipelines?.name || 'Sem Pipeline';
    
    if (!acc[stageId]) {
      acc[stageId] = {
        stageId,
        stageName,
        pipelineName,
        leads: []
      };
    }
    
    // Filtrar leads que não estão na campanha
    const isAlreadyAdded = campaignLeads.some(campaignLead => campaignLead.contact_id === lead.id);
    
    // Filtro por canal (opcional)
    const matchesChannel = showAllLeads || !campaign?.channel || lead.source === campaign?.channel;
    
    // Filtro por busca
    const matchesSearch = !searchQuery || 
                         lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         lead.phone?.includes(searchQuery) ||
                         lead.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Debug: Log para cada lead
    console.log(`🔍 Lead ${lead.name} (${lead.source}):`, {
      isAlreadyAdded,
      matchesChannel,
      matchesSearch,
      campaignChannel: campaign?.channel,
      showAllLeads
    });
    
    if (!isAlreadyAdded && matchesChannel && matchesSearch) {
      acc[stageId].leads.push(lead);
    }
    
    return acc;
  }, {} as Record<string, { stageId: string; stageName: string; pipelineName: string; leads: Lead[] }>);

  // Debug: Log final
  console.log('📋 Resultado final:', {
    totalLeads: availableLeads.length,
    campaignLeads: campaignLeads.length,
    showAllLeads,
    campaignChannel: campaign?.channel,
    leadsByStage: Object.keys(leadsByStage).map(key => ({
      stage: leadsByStage[key].stageName,
      count: leadsByStage[key].leads.length
    }))
  });

  // Função para selecionar todos os leads de um estágio
  const handleSelectAllLeadsInStage = (stageId: string) => {
    const stageLeads = leadsByStage[stageId]?.leads || [];
    const stageLeadIds = stageLeads.map(lead => lead.id);
    
    // Verificar se todos os leads do estágio já estão selecionados
    const allSelected = stageLeadIds.every(id => selectedLeads.includes(id));
    
    if (allSelected) {
      // Desmarcar todos
      setSelectedLeads(prev => prev.filter(id => !stageLeadIds.includes(id)));
    } else {
      // Marcar todos (removendo duplicatas)
      setSelectedLeads(prev => {
        const combined = [...prev, ...stageLeadIds];
        return combined.filter((id, index) => combined.indexOf(id) === index);
      });
    }
  };

  // Verificar se todos os leads de um estágio estão selecionados
  const isStageFullySelected = (stageId: string) => {
    const stageLeads = leadsByStage[stageId]?.leads || [];
    const stageLeadIds = stageLeads.map(lead => lead.id);
    return stageLeadIds.length > 0 && stageLeadIds.every(id => selectedLeads.includes(id));
  };

  // Verificar se pelo menos um lead de um estágio está selecionado
  const isStagePartiallySelected = (stageId: string) => {
    const stageLeads = leadsByStage[stageId]?.leads || [];
    const stageLeadIds = stageLeads.map(lead => lead.id);
    return stageLeadIds.some(id => selectedLeads.includes(id));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Campanha não encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/campaigns/disparos')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Editar Campanha</Typography>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Informações da Campanha */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações da Campanha
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Nome
                </Typography>
                <Typography variant="body1">
                  {campaign.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Canal
                </Typography>
                <Chip label={campaign.channel} color="primary" size="small" />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label={campaign.status} 
                  color={campaign.status === 'draft' ? 'default' : 'primary'} 
                  size="small" 
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Destinatários Atuais
                </Typography>
                <Typography variant="h6" color="primary">
                  {campaignLeads.length}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Mensagem
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderRadius: 1, 
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="body2">
                  {campaign.message}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Adicionar Leads */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Adicionar Leads por Estágio do CRM
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Canal da campanha: <Chip label={campaign.channel} size="small" color="primary" />
              </Typography>

              {/* Controles de Filtro */}
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Filtros
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showAllLeads}
                          onChange={(e) => setShowAllLeads(e.target.checked)}
                        />
                      }
                      label="Mostrar todos os leads (não filtrar por canal)"
                    />
                  </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Total de leads disponíveis: {availableLeads.length} | 
                  Leads filtrados: {Object.values(leadsByStage).reduce((sum, stage) => sum + stage.leads.length, 0)}
                </Typography>
              </Box>

              <TextField
                fullWidth
                placeholder="Buscar leads por nome, telefone ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 500, overflow: 'auto', mb: 2 }}>
                {Object.keys(leadsByStage).length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Nenhum lead disponível para o canal {campaign.channel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tente ativar "Mostrar todos os leads" ou verifique se existem leads cadastrados no sistema.
                    </Typography>
                  </Box>
                ) : (
                  Object.values(leadsByStage).map((stage) => (
                    <Box key={stage.stageId} sx={{ mb: 3 }}>
                      {/* Header do Estágio */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {stage.pipelineName} - {stage.stageName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stage.leads.length} lead(s) disponível(is)
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant={isStageFullySelected(stage.stageId) ? "contained" : "outlined"}
                          onClick={() => handleSelectAllLeadsInStage(stage.stageId)}
                          disabled={stage.leads.length === 0}
                        >
                          {isStageFullySelected(stage.stageId) ? 'Desmarcar Todos' : 'Marcar Todos'}
                        </Button>
                      </Box>

                      {/* Lista de Leads do Estágio */}
                      {stage.leads.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                          Nenhum lead disponível neste estágio
                        </Typography>
                      ) : (
                        stage.leads.map((lead) => (
                          <Box
                            key={lead.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                              ml: 2,
                              border: '1px solid',
                              borderColor: selectedLeads.includes(lead.id) ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              mb: 1,
                              cursor: 'pointer',
                              bgcolor: selectedLeads.includes(lead.id) ? 'primary.dark' : 'transparent',
                              '&:hover': {
                                bgcolor: selectedLeads.includes(lead.id) ? 'primary.main' : 'background.default'
                              }
                            }}
                            onClick={() => handleLeadSelection(lead.id, !selectedLeads.includes(lead.id))}
                          >
                            <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {lead.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {lead.phone || lead.email || 'Sem contato'}
                              </Typography>
                            </Box>
                            <Chip label={lead.source} size="small" />
                          </Box>
                        ))
                      )}
                    </Box>
                  ))
                )}
              </Box>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddLeads}
                disabled={selectedLeads.length === 0 || saving}
                fullWidth
              >
                {saving ? <CircularProgress size={20} /> : `Adicionar ${selectedLeads.length} Lead(s)`}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Leads da Campanha */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Leads da Campanha ({campaignLeads.length})
              </Typography>

              {campaignLeads.length === 0 ? (
                <Typography color="text.secondary" align="center">
                  Nenhum lead adicionado a esta campanha
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Contato</TableCell>
                        <TableCell>Fonte</TableCell>
                        <TableCell>Estágio</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaignLeads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {lead.contact?.name || 'Nome não disponível'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {lead.contact?.phone || lead.contact?.email || 'Sem contato'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={lead.contact?.source || 'sem fonte'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {lead.status}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remover">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveLead(lead.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DisparosEdit; 